import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";import { Search, ShieldCheck, Repeat, Users, Lock, Sparkles, Star, Heart, BookOpen } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import LipstickIcon from "@/components/LipstickIcon";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PARTNERS } from "@/data/partners";

const BADGES = [
  { icon: ShieldCheck, title: "AUTHENTIC", sub: "Products Only" },
  { icon: Repeat, title: "BUY, SELL", sub: "or Trade" },
  { icon: Users, title: "BEAUTY", sub: "Community" },
  { icon: Lock, title: "SAFE & SECURE", sub: "Transactions" },
];

const CATEGORY_TILES = [
  { label: "Perfume", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/5ac1adc52e7b1608b06d8d9ccfb6b435f309ff464275121b122babe3d764498c.png", to: "/shop?category=Fragrance" },
  { label: "Lipstick", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/a28e07df7b3332ce65a11a51179219c23c81bdc06d8a7ef04e0445355ccb67cb.png", to: "/shop?category=Lips" },
  { label: "Foundation", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/ef0138366671d7c632737630d5c1e17c509485355c33a686fd734826854b447b.png", to: "/shop?search=foundation" },
  { label: "Blush", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/b99919d8b01278c41e1041b191dd34ac665f6451f0e3ab3fc773419c0fea8ed9.png", to: "/shop?search=blush" },
  { label: "Skincare", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/d2682491322f1d1828c632c305929c93e57c10e6f6f06cbfc8af69267d4bb26d.png", to: "/shop?category=Skincare" },
  { label: "Eyeshadow", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/ec1bde76b0ecd818c4b80bc07f840b749e9d589c31e9a276aaf25b5e33cb0a5b.png", to: "/shop?category=Eyes" },
  { label: "Hair Dye", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/d1bd376edcf453162da372110d437d2ef75d1a5829359113c1e680eb3921194e.png", to: "/shop?search=hair" },
  { label: "Brushes", img: "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/ce1461d69cca359ef9a66c22a90fd9bc0fa5ba6b0d30e8c1657074a00ed2f4dd.png", to: "/shop?category=Tools" },
];

const HOME_FAQS = [
  { q: "Is it free to shop and sign up?", a: "Yes! Browsing and buying on Beauty Trade Club is completely free. Sellers only pay a 15% fee on completed sales — there are no listing or sign-up fees." },
  { q: "How am I protected when I buy?", a: "Every payment is held in escrow and only released to the seller after you confirm your item arrived as described. If something's wrong, you can report an issue and our team steps in before any money changes hands." },
  { q: "Can I sell used makeup?", a: "Absolutely. You can list new or gently-used makeup, skincare, fragrance and tools. Apply to become a verified seller, upload your photos, and you keep 85% of every sale." },
  { q: "Are the sellers and products authentic?", a: "Every seller is manually reviewed and approved before listing, and buyers leave honest star reviews after each purchase — so quality and trust stay high across the club." },
];

export default function Home() {
  const navigate = useNavigate();
  const [picks, setPicks] = useState([]);
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");

  usePageMeta(
    "Beauty Trade Club — Buy, Sell & Trade Makeup Online",
    "Shop authentic new & gently-used makeup, skincare and fragrance from verified sellers. Buy, sell and trade beauty with secure, escrow-protected checkout on Beauty Trade Club.",
    "/"
  );

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: HOME_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "home-faq-schema";
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { document.getElementById("home-faq-schema")?.remove(); };
  }, []);

  useEffect(() => {
    api.get("/picks").then(({ data }) => setPicks(data.slice(0, 4))).catch(() => {});
    api.get("/blog?limit=3").then(({ data }) => setPosts(data || [])).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?search=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/f2343412b463fe5ef193dbc9c132c60679ebff55c8a051ec497cea1e4a97820a.png')" }}
      >
        {/* soft overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/55 to-white pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 pt-12 pb-6 flex flex-col items-center text-center">
          <div className="flex items-center gap-2 text-[#E8956B] text-sm sm:text-base font-extrabold uppercase tracking-[0.3em] mb-6 animate-fade-up">
            <Sparkles size={18} /> The Beauty Marketplace
          </div>
          <h1 className="text-6xl sm:text-7xl lg:text-8xl tracking-tight animate-fade-up">
            <span className="font-fun text-white block leading-none text-8xl sm:text-9xl lg:text-[11rem]" style={{ textShadow: "0 0 18px rgba(232,149,107,0.65), 0 0 40px rgba(232,149,107,0.4), 0 2px 10px rgba(0,0,0,0.25)" }}>Beauty</span>
            <span className="font-display font-black text-neutral-900 block mt-1">Trade Club</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl font-bold text-neutral-900 max-w-xl animate-fade-up" style={{ animationDelay: "0.1s" }}>
            Buy, Sell &amp; Trade Your Favorite Beauty Finds
          </p>
          <p className="mt-3 text-base sm:text-lg text-neutral-800 max-w-xl animate-fade-up" style={{ animationDelay: "0.15s" }} data-testid="home-sell-tagline">
            Sell your never-used and lightly-used makeup for profit 💸
          </p>

          {/* Feature badges */}
          <div className="mt-8 w-full grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl animate-fade-up" style={{ animationDelay: "0.2s" }}>
            {BADGES.map((b) => (
              <div key={b.title} className="flex flex-col items-center gap-2 py-4">
                <b.icon size={30} className="text-[#E8956B]" strokeWidth={1.5} />
                <div className="text-sm font-bold text-neutral-900 tracking-wide">{b.title}</div>
                <div className="text-sm text-neutral-900 -mt-1">{b.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="max-w-4xl mx-auto px-6 -mt-2 mb-12">
        <form onSubmit={doSearch} className="flex items-center bg-white border border-neutral-300 rounded-full p-2 shadow-[0_10px_30px_rgba(0,0,0,0.06)] focus-within:border-[#E8956B] transition-all">
          <Search size={20} className="text-neutral-400 ml-3" />
          <input
            data-testid="home-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search for products, brands or keywords..."
            className="bg-transparent border-none outline-none text-neutral-900 px-4 py-2 w-full placeholder-neutral-400"
          />
          <button data-testid="home-search-submit" type="submit" className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2 btn-glow transition-all">
            Search
          </button>
        </form>
      </section>

      {/* SHOP BY CATEGORY */}
      <section className="max-w-7xl mx-auto px-6 pb-4" data-testid="shop-by-category">
        <div className="text-center mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold flex items-center justify-center gap-2">
            Shop by Category <Sparkles size={20} className="text-[#E8956B]" />
          </h2>
          <p className="text-neutral-500 text-sm mt-2">Explore the marketplace by what you love</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {CATEGORY_TILES.map((c) => (
            <div key={c.label} data-testid={`category-tile-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="group flex flex-col">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-neutral-100 border border-neutral-200">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <h3 className="mt-3 text-center font-display text-lg font-bold text-neutral-900">{c.label}</h3>
              <button
                data-testid={`category-shop-now-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => navigate(c.to)}
                className="mt-2 w-full border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full py-2 text-sm transition-all"
              >
                Shop Now
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* PICKS */}
      {picks.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12" data-testid="home-picks-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              Picks <Star size={18} className="text-[#E8956B]" fill="currentColor" />
            </h2>
            <button data-testid="view-picks-btn" onClick={() => navigate("/picks")} className="flex items-center gap-2 text-[#E8956B] font-semibold text-sm hover:gap-3 transition-all">
              See All <LipstickIcon size={20} />
            </button>
          </div>
          <p className="text-neutral-500 text-sm mb-8">Loved & recommended by our influencers</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {picks.map((pick) => (
              <div
                key={pick.id}
                data-testid={`home-pick-${pick.id}`}
                onClick={() => navigate(`/product/${pick.product.id}${pick.influencer_code ? `?ref=${pick.influencer_code}` : ""}`)}
                className="bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:border-[#E8956B]/60 hover:shadow-[0_14px_34px_rgba(0,0,0,0.08)] transition-all duration-300 cursor-pointer group flex flex-col"
              >
                <div className="relative aspect-square overflow-hidden bg-neutral-100">
                  <span className="absolute top-3 left-3 z-10 bg-[#E8956B] text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider flex items-center gap-1">
                    <Star size={10} fill="currentColor" /> {pick.influencer_name?.split(" ")[0]}
                  </span>
                  <img src={imgUrl(pick.product.images?.[0])} alt={pick.product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  {pick.note && <p className="text-xs text-neutral-500 italic line-clamp-2 mb-2">“{pick.note}”</p>}
                  <h3 className="font-semibold text-neutral-900 text-sm leading-tight line-clamp-1">{pick.product.title}</h3>
                  <span className="text-lg font-bold text-neutral-900">${(pick.product.sale_price || pick.product.price).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* IN GOOD COMPANY — partner strip */}
      <section className="max-w-5xl mx-auto px-6 py-8" data-testid="home-we-love-strip">
        <div className="bg-neutral-50 border border-neutral-200 rounded-3xl px-6 py-8 flex flex-col items-center text-center">
          <div className="flex items-center gap-2 text-[#E8956B] text-xs font-extrabold uppercase tracking-[0.3em]">
            <Heart size={14} fill="currentColor" /> In Good Company
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-neutral-900 mt-2">
            Beauty sites we love
          </h2>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-8">
            {PARTNERS.map((p) => (
              <img
                key={p.slug}
                src={p.logo}
                alt={p.name}
                data-testid={`home-partner-logo-${p.slug}`}
                className="h-20 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
              />
            ))}
          </div>
          <button
            data-testid="home-we-love-btn"
            onClick={() => navigate("/we-love")}
            className="mt-6 border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full px-6 py-2 text-sm transition-all"
          >
            See all our favorites
          </button>
        </div>
      </section>

      {/* CLEAN & SAFE TEASER */}
      <section className="max-w-7xl mx-auto px-6 py-8" data-testid="home-clean-teaser">
        <div className="flex items-center gap-2 text-[#E8956B] text-xs font-extrabold uppercase tracking-[0.3em] mb-3">
          <Sparkles size={15} /> Clean · Safe · Trusted
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-neutral-900 mb-5" data-testid="home-clean-title">How To Clean?</h2>
        <Link to="/clean-guide" className="group flex items-center gap-4 rounded-2xl border border-[#E8956B]/30 bg-[#E8956B]/5 hover:bg-[#E8956B]/10 p-5 transition-all">
          <span className="text-3xl">🧼</span>
          <div className="flex-1">
            <p className="font-display text-lg font-bold text-neutral-900">New to pre-loved beauty? See how we keep it clean &amp; safe</p>
            <p className="text-sm text-neutral-500">Our simple guide to sanitizing and shopping used makeup with confidence.</p>
          </div>
          <span className="text-[#CE744A] font-bold group-hover:translate-x-1 transition-transform">Read the guide →</span>
        </Link>
      </section>

      {/* LATEST FROM THE BLOG */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12" data-testid="home-blog-strip">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-[#E8956B] text-xs font-extrabold uppercase tracking-[0.3em] mb-2">
                <BookOpen size={15} /> The Club Journal
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-neutral-900">Latest from the blog</h2>
            </div>
            <button onClick={() => navigate("/blog")} data-testid="home-blog-viewall" className="hidden sm:inline text-sm font-bold text-[#CE744A] hover:underline">
              View all →
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {posts.map((p) => (
              <button
                key={p.id}
                data-testid={`home-blog-card-${p.slug}`}
                onClick={() => navigate(`/blog/${p.slug}`)}
                className="group text-left bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="aspect-[16/10] bg-neutral-100 overflow-hidden">
                  {p.image_url ? (
                    <img src={imgUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#E8956B]"><BookOpen size={36} /></div>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-xs text-neutral-400 font-semibold">{p.date}</p>
                  <h3 className="font-display text-lg font-bold text-neutral-900 mt-1 leading-snug line-clamp-2">{p.title}</h3>
                  <p className="text-sm text-neutral-500 mt-1.5 line-clamp-2">{p.excerpt}</p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-14" data-testid="home-faq">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-3">Common Questions</h2>
        <p className="text-neutral-500 text-sm text-center mb-8">Everything you need to know before you shop or sell</p>
        <div className="space-y-3">
          {HOME_FAQS.map((f, i) => (
            <details key={i} data-testid={`home-faq-${i}`} className="group bg-white border border-neutral-200 rounded-2xl px-6 py-4 open:border-[#E8956B]/50 transition-colors">
              <summary className="flex items-center justify-between cursor-pointer font-semibold text-neutral-900 list-none">
                {f.q}
                <span className="text-[#E8956B] text-2xl leading-none transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-neutral-600 leading-relaxed text-sm">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="text-center mt-8">
          <button data-testid="home-faq-more-btn" onClick={() => navigate("/how-it-works")} className="text-[#E8956B] font-semibold text-sm hover:underline">
            See how it works &amp; more FAQs →
          </button>
        </div>
      </section>
    </div>
  );
}
